import { jwtDecode } from 'jwt-decode';
import { memoize } from 'lodash';

export const HTTP_SESSION_KEYS = {
  accessToken: 'access-token',
  tenant: 'x-tenant',
} as const;

const CROSS_TENANT_PERMISSION = 'crosstenant:*';

let sessionEpoch = 0;

export function getHttpSessionEpoch(): number {
  return sessionEpoch;
}

function readJsonSessionValue(key: string): string | null {
  if (typeof sessionStorage === 'undefined') return null;

  const raw = sessionStorage.getItem(key);
  if (!raw) return null;

  try {
    const value = JSON.parse(raw);
    return typeof value === 'string' && value.length > 0 ? value : null;
  } catch {
    return null;
  }
}

function writeJsonSessionValue(key: string, value: string | null): void {
  if (typeof sessionStorage === 'undefined') return;

  if (value === null || value === '') {
    sessionStorage.removeItem(key);
  } else {
    sessionStorage.setItem(key, JSON.stringify(value));
  }

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('session-change', { detail: { key } }));
  }
}

export function getAccessToken(): string | null {
  return readJsonSessionValue(HTTP_SESSION_KEYS.accessToken);
}

export function setAccessToken(token: string | null, epoch = sessionEpoch): void {
  if (epoch !== sessionEpoch) return;
  writeJsonSessionValue(HTTP_SESSION_KEYS.accessToken, token);
}

export function setTenantOverride(tenant: string | null): void {
  writeJsonSessionValue(HTTP_SESSION_KEYS.tenant, tenant);
}

const cachedPermissionDecode = memoize(jwtDecode<{ permissions?: string[] }>);

export function tokenHasCrossTenantPermission(token: string | null): boolean {
  if (!token) return false;
  try {
    const decoded = cachedPermissionDecode(token);
    return decoded.permissions?.includes(CROSS_TENANT_PERMISSION) ?? false;
  } catch {
    return false;
  }
}

export function getHttpSessionHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  const accessToken = getAccessToken();
  const tenant = readJsonSessionValue(HTTP_SESSION_KEYS.tenant);

  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  // Backend 403s any X-Tenant unless the JWT has crosstenant:*, even if it matches the token tenant.
  if (tenant && tokenHasCrossTenantPermission(accessToken)) headers['X-Tenant'] = tenant;

  return headers;
}

export function clearHttpSession(): void {
  sessionEpoch += 1;
  writeJsonSessionValue(HTTP_SESSION_KEYS.accessToken, null);
  writeJsonSessionValue(HTTP_SESSION_KEYS.tenant, null);
}
